import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

interface PrintDocumentResult {
  document: {
    id: string;
    file_name: string;
    status: string;
  };
  downloadUrl: string;
}

type BillingDocumentType = 'credit_note' | 'debit_note' | 'quote' | 'write_off' | 'advance';

/**
 * Hook to generate and download a billing document PDF (credit note, debit note, quote, write-off, or advance).
 *
 * Calls the corresponding generate-pdf endpoint:
 * - POST /billing/credit-notes/:id/generate-pdf
 * - POST /billing/debit-notes/:id/generate-pdf
 * - POST /billing/quotes/:id/generate-pdf
 * - POST /billing/write-offs/:id/generate-pdf
 * - POST /billing/advances/:id/generate-pdf
 *
 * Returns a presigned download URL for the generated PDF.
 */
export function usePrintBillingDocument(documentType: BillingDocumentType) {
  const pathMap: Record<BillingDocumentType, string> = {
    credit_note: 'credit-notes',
    debit_note: 'debit-notes',
    quote: 'quotes',
    write_off: 'write-offs',
    advance: 'advances',
  };

  return useMutation({
    mutationFn: async (recordId: string): Promise<PrintDocumentResult> => {
      const path = pathMap[documentType];
      const { data } = await apiClient.post<ApiResponse<PrintDocumentResult>>(
        `/billing/${path}/${recordId}/generate-pdf`,
        {},
      );
      return data.data;
    },
  });
}
